import { useCallback } from "react";
import {
  forceSimulation,
  forceCollide,
  forceManyBody,
  SimulationNodeDatum,
} from "d3-force";
import { useReactFlow } from "@xyflow/react";
import type { Node } from "@xyflow/react";

type SimNodeType = SimulationNodeDatum & {
  id: string;
  x: number;
  y: number;
  width?: number;
  height?: number;
};

/**
 * Hook to position a single new node in a non-colliding position
 * using D3 force simulation while keeping all other nodes fixed
 */
export function useSingleNodeForceLayout() {
  const { getNodes, setNodes } = useReactFlow();

  const findNonCollidingPosition = useCallback(
    (nodeId: string): Promise<{ x: number; y: number }> => {
      return new Promise((resolve) => {
        const nodes = getNodes();
        const targetNode = nodes.find((n) => n.id === nodeId);

        if (!targetNode) {
          resolve({ x: 0, y: 0 });
          return;
        }

        // Create simulation nodes - existing nodes are fixed
        const simulationNodes: SimNodeType[] = nodes.map((node) => {
          const isTarget = node.id === nodeId;
          const measured = (node as any)?.measured as
            | { width?: number; height?: number }
            | undefined;
          return {
            id: node.id,
            x: node.position.x,
            y: node.position.y,
            width: measured?.width || (node as any)?.width || 150,
            height: measured?.height || (node as any)?.height || 50,
            // Fixed nodes don't move (fx and fy lock position)
            fx: isTarget ? undefined : node.position.x,
            fy: isTarget ? undefined : node.position.y,
          };
        });

        const targetSimNode = simulationNodes.find((n) => n.id === nodeId);
        if (!targetSimNode) {
          resolve({ x: 0, y: 0 });
          return;
        }

        // Create force simulation
        const simulation = forceSimulation<SimNodeType>(simulationNodes)
          // Repulsion force to push nodes apart
          .force("charge", forceManyBody().strength(-500))
          // Collision detection based on node size
          .force(
            "collide",
            forceCollide<SimNodeType>()
              .radius(
                (d) =>
                  Math.max((d.width || 150) / 2, (d.height || 50) / 2) + 20,
              )
              .strength(1),
          )
          .alpha(1) // Start with high energy
          .alphaDecay(0.05) // Slower decay for smoother movement
          .velocityDecay(0.4) // Friction
          .stop();

        // Run simulation manually for a fixed number of ticks
        const maxTicks = 100;
        let tickCount = 0;

        const runTick = () => {
          simulation.tick();
          tickCount++;

          // Update the actual node position in React Flow
          setNodes((currentNodes) =>
            currentNodes.map((node) => {
              if (node.id === nodeId) {
                return {
                  ...node,
                  position: {
                    x: targetSimNode.x ?? node.position.x,
                    y: targetSimNode.y ?? node.position.y,
                  },
                };
              }
              return node;
            }),
          );

          // Continue until simulation stabilizes or max ticks reached
          if (simulation.alpha() > 0.01 && tickCount < maxTicks) {
            requestAnimationFrame(runTick);
          } else {
            // Simulation complete
            resolve({
              x: targetSimNode.x ?? 0,
              y: targetSimNode.y ?? 0,
            });
          }
        };

        runTick();
      });
    },
    [getNodes, setNodes],
  );

  return { findNonCollidingPosition };
}

export default useSingleNodeForceLayout;
